import { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../hooks/useLanguage';

function isAndroid() {
  return typeof navigator !== 'undefined' && /Android/i.test(navigator.userAgent);
}

export default function SearchableSelect({
  options,
  value,
  onChange,
  placeholder,
  labelKey = 'name',
  valueKey = 'id'
}) {
  const { lang } = useLanguage();
  const labels = lang === 'EN'
    ? { choose: 'Select…', clear: 'Clear', close: 'Close', empty: 'No matches found' }
    : { choose: 'Выберите…', clear: 'Очистить', close: 'Закрыть', empty: 'Ничего не найдено' };
  const resolvedPlaceholder = placeholder || labels.choose;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const modalContentRef = useRef(null);
  const modalViewportRef = useRef(null);

  const selectedOption = options.find(opt => opt[valueKey] === value);

  const sortedFiltered = useMemo(() => {
    return options
      .filter(opt =>
        opt[labelKey]?.toLowerCase().includes(query.toLowerCase())
      )
      .sort((a, b) =>
        a[labelKey].toLowerCase().localeCompare(b[labelKey].toLowerCase())
      );
  }, [options, query, labelKey]);

  useEffect(() => {
    if (!value) setQuery('');
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedOutsideModal =
        modalOpen && modalContentRef.current && !modalContentRef.current.contains(event.target);
      const clickedOutsideDropdown =
        open && containerRef.current && !containerRef.current.contains(event.target);

      if (clickedOutsideModal) {
        setModalOpen(false);
        if (query && sortedFiltered.length === 0) {
          setQuery('');
          onChange(null);
        }
      }

      if (clickedOutsideDropdown) {
        setOpen(false);
        if (query && sortedFiltered.length === 0) {
          setQuery('');
          onChange(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, modalOpen, onChange, query, sortedFiltered.length]);

  useLayoutEffect(() => {
    if (!modalOpen) return undefined;

    const viewport = window.visualViewport;
    const overlay = modalViewportRef.current;
    const dialog = modalContentRef.current;
    const trigger = inputRef.current;
    const previousOverflow = document.body.style.overflow;
    const previousOverscroll = document.body.style.overscrollBehavior;

    // The keyboard can resize and pan the visual viewport without changing dvh.
    const updateViewport = () => {
      if (!overlay) return;
      Object.assign(overlay.style, {
        top: `${viewport?.offsetTop ?? 0}px`,
        left: `${viewport?.offsetLeft ?? 0}px`,
        width: `${viewport?.width ?? window.innerWidth}px`,
        height: `${viewport?.height ?? window.innerHeight}px`,
      });
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setModalOpen(false);
    };

    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    updateViewport();
    viewport?.addEventListener('resize', updateViewport);
    viewport?.addEventListener('scroll', updateViewport);
    window.addEventListener('resize', updateViewport);
    document.addEventListener('keydown', handleKeyDown);

    // Focus the dialog, not its search input: choosing an item needs no keyboard.
    const frame = window.requestAnimationFrame(() => {
      dialog?.focus({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(frame);
      viewport?.removeEventListener('resize', updateViewport);
      viewport?.removeEventListener('scroll', updateViewport);
      window.removeEventListener('resize', updateViewport);
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      document.body.style.overscrollBehavior = previousOverscroll;
      if (trigger?.isConnected && (dialog?.contains(document.activeElement) || document.activeElement === document.body)) {
        trigger?.focus({ preventScroll: true });
      }
    };
  }, [modalOpen]);

  const handleSelect = (opt) => {
    onChange(opt[valueKey]);
    // Let selectedOption provide the visible label so it updates immediately
    // when the site language changes.
    setQuery('');
    setOpen(false);
    setModalOpen(false);
  };

  const handleClear = () => {
    setQuery('');
    onChange(null);
  };

  const isAndroidDevice = isAndroid();

  const mainInputValue = query !== '' ? query : selectedOption?.[labelKey] || '';

  // 🔕 Полное отключение focus / ring / outline
  const NO_OUTLINE =
    'outline-none focus:outline-none focus:ring-0 focus:ring-offset-0 focus-visible:outline-none';

  return (
    <div ref={containerRef}>
      <div className="relative">
        <div className="relative">
          {isAndroidDevice ? (
            <button
              ref={inputRef}
              type="button"
              onClick={() => setModalOpen(true)}
              aria-label={resolvedPlaceholder}
              aria-haspopup="dialog"
              aria-expanded={modalOpen}
              className={`searchable-select-trigger w-full px-2 pr-8 py-2 text-base text-left border rounded ${NO_OUTLINE}`}
            >
              {mainInputValue || <span className="searchable-select-trigger__placeholder">{resolvedPlaceholder}</span>}
            </button>
          ) : <input
            ref={inputRef}
            type="text"
            value={mainInputValue}
            onChange={(e) => {
              const val = e.target.value;
              setQuery(val);
              setOpen(true);
              if (val === '') onChange(null);
            }}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            placeholder={resolvedPlaceholder}
            aria-label={resolvedPlaceholder}
            className={[
              'w-full px-2 pr-8 bg-gray-800 text-white text-base border border-gray-600 transition-all duration-200',
              open ? 'rounded-t' : 'rounded',
              NO_OUTLINE,
              'py-2',
            ].join(' ')}
          />}

          {(query || value) && (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-gray-700 hover:bg-gray-600 rounded-full w-6 h-6 flex items-center justify-center"
              title={labels.clear}
              aria-label={labels.clear}
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                stroke="currentColor"
                fill="none"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="block"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>

        {/* Inline dropdown (iOS/Desktop only) */}
        {open && !isAndroidDevice && (
          <div
            className="w-full absolute bg-gray-900 border border-gray-700 rounded-b shadow-lg z-50"
            style={{
              maxHeight: '40vh',
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              touchAction: 'manipulation',
            }}
            onMouseDown={(e) => e.preventDefault()}
          >
            {sortedFiltered.length > 0 ? (
              sortedFiltered.map((opt, idx) => (
                <div
                  key={`${opt[valueKey]}::${opt[labelKey]}::${idx}`}
                  onClick={() => handleSelect(opt)}
                  className="px-3 py-2 text-sm bg-transparent hover:bg-[var(--surface-muted)] transition-colors cursor-pointer text-white"
                >
                  {opt[labelKey]}
                </div>
              ))
            ) : (
              <div className="px-3 py-2 text-sm text-gray-500">
                {labels.empty}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Android-only modal dropdown. Portaling avoids transformed/scrolling ancestors. */}
      {modalOpen && isAndroidDevice && typeof document !== 'undefined' && createPortal(
        <div
          ref={modalViewportRef}
          className="searchable-select-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setModalOpen(false);
          }}
        >
          <div
            ref={modalContentRef}
            className="searchable-select-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-label={resolvedPlaceholder}
            tabIndex={-1}
          >
            <div className="searchable-select-modal__header">
              <div className="relative flex items-center">
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={resolvedPlaceholder}
                  aria-label={resolvedPlaceholder}
                  className={[
                    'w-full px-2 pr-8 bg-gray-800 text-white border border-gray-600 rounded transition-all duration-200',
                    NO_OUTLINE,
                    'py-2',
                  ].join(' ')}
                />

                {(query || value) && (
                  <button
                    type="button"
                    onClick={handleClear}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-white bg-gray-700 hover:bg-gray-600 rounded-full w-6 h-6 flex items-center justify-center"
                    title={labels.clear}
                    aria-label={labels.clear}
                  >
                    <svg
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      stroke="currentColor"
                      fill="none"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="block"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                )}
              </div>

              <button
                type="button"
                onClick={() => {
                  setModalOpen(false);
                }}
                className="searchable-select-modal__close"
              >
                {labels.close}
              </button>
            </div>

            <div
              className="searchable-select-modal__options"
              role="listbox"
            >
              {sortedFiltered.length > 0 ? (
                sortedFiltered.map((opt, idx)=> (
                  <button
                    type="button"
                    key={`${opt[valueKey]}::${opt[labelKey]}::${idx}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelect(opt);
                    }}
                    className="searchable-select-modal__option"
                    role="option"
                    aria-selected={opt[valueKey] === value}
                  >
                    {opt[labelKey]}
                  </button>
                ))
              ) : (
                <div className="searchable-select-modal__empty">
                  {labels.empty}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
